import React from "react";
import PropTypes from "prop-types";
import { Button, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import Cookies from 'js-cookie';
import { formatDate, getRelativeTime } from "./time";

export const LOGIN_EXPIRATION = 30 * 60 * 1000; // 30 minutes

const subdomainDomains = [
    "saberdevusa.xyz", // dev
    "saberastro.com", // prod
    "saber-sbms.basec.io" // BAH
];

export const getBaseUrl = (isCAC = false) => {
    if (import.meta.env.VITE_BASE_URL) return import.meta.env.VITE_BASE_URL;
    // Get the current domain and determine the base URL
    const currentDomain = globalThis.location.hostname;

    if(currentDomain === 'localhost') return 'http://localhost:9001';
    else if(isCAC && currentDomain.includes("saberastro.com"))
        return `https://${currentDomain.replace(/^[^.]+/, 'nps')}:444`; // CAC logins on saberastro.com only
    else if(subdomainDomains.some((d) => currentDomain.includes(d)))
        return `https://${currentDomain.replace(/^[^.]+/, 'nps')}`; // replace any subdomain (e.g. intern1, sbms) with nps
    else // high side deployments use paths not subdomains
        return `https://${currentDomain}/nps`;
};

export const getUDLUrl = () => {
    return "https://unifieddatalibrary.com"; // used to return TEST UDL (test.unifieddatalibrary.com) for non-prod but we use prod UDL in dev now
};

export const getDomainUrl = (subdomain) => {
    const isLocalDev = globalThis.location.hostname === 'localhost';
    if (isLocalDev) {
        if (subdomain === 'spacefana') {
            return `http://localhost:3000`;
        }
        if (subdomain === 'sbms') {
            return `http://localhost:3001`;
        }
        if (subdomain === 'nps') {
            return `http://localhost:9001`;
        }
        if (subdomain === 'sewol') {
            return `http://localhost:5173`;
        }
        else {
            return `https://${subdomain}.saberdevusa.xyz`;
        }
    }
    if (subdomainDomains.some((d) => globalThis.location.hostname.includes(d))) {
        return globalThis.location.origin.replace(/^(https?:\/\/)[^.]+/, `$1${subdomain}`); // replace any subdomain (e.g. intern1, sbms) with the requested one
    }
    if (!globalThis.location.hostname.includes("sbms.")) { // high side deployments use paths not subdomains
        return `${globalThis.location.origin}/${subdomain}`;
    }
    return globalThis.location.origin.replaceAll("sbms", subdomain);
};

export const getPathUrl = (path, subdomain = "sbms") => {
    // path is: /dashboards
    // origin is: https://sbms.saberdevusa.xyz
    // high side origin is: https://domain/sbms
    const origin = getDomainUrl(subdomain);
    return origin + path;
};

export const plural = (word, count) => count === 1 ? word : `${word}s`;

// Format satellite name consistently as "SatNo - CommonName" or just "SatNo" if no name
export const getSatelliteName = (satNoOrObj, commonName = null) => {
    // Support both object format {id, name} and separate parameters (satNo, commonName)
    if (typeof satNoOrObj === 'object' && satNoOrObj !== null) {
        const {id, name} = satNoOrObj;
        if (name === "None") return name;
        return name ? `${id} - ${name}` : id;
    }
    // Direct parameters
    const satNo = satNoOrObj;
    return commonName ? `${satNo} - ${commonName}` : satNo;
};

export const checkMessageOrigin = (event, type="spacefana") => {
    const url = getDomainUrl(type);
    if(event.origin !== url) {
        console.warn(`Got ${event.data.type} message but from incorrect origin`, event.origin, "vs", url);
        return false;
    }
    return true;
};

export const TooltipButton = ({tooltip, props, wrapDiv=false, children, placement="bottom", tooltipClass=""}) => {
    const button = <Button
        data-testid={tooltip}
        {...props}
    >
        {children}
    </Button>;
    return (
        <OverlayTrigger
            placement={placement}
            overlay={
                tooltip ? <Tooltip className={tooltipClass}>
                    {tooltip}
                </Tooltip> : <></>
            }
        >
            {wrapDiv ? <div style={{width: "fit-content"}}>{button}</div> : button}
        </OverlayTrigger>
    );
};
TooltipButton.propTypes = {
    tooltip: PropTypes.any.isRequired,
    props: PropTypes.object,
    wrapDiv: PropTypes.bool,
    children: PropTypes.node,
    placement: PropTypes.string,
    tooltipClass: PropTypes.string,
};

export const isValidEmail = (email) => {
    // simplified and doesn't use regex 
    // because of sonarqube "vulnerable to super-linear runtime due to backtracking" warning
    if(!email) return false;
    const i1 = email.indexOf("@");
    if(i1 <= 0) return false;
    const sub = email.substring(i1);
    const i2 = sub.indexOf(".");
    return i2 > 0 && i2 < sub.length-1;
};

export const isVowel = (x) => {
    return /[aeiouAEIOU]/.test(x);
};

// returns "a {word}" if consonant, "an {word}" if vowel
export const vowelize = (word) => {
    if (word?.length > 0) {
        return (isVowel(word[0]) ? "an " : "a ") + word;
    }
    return word;
};

export const LoadingSpinner = () => (
    <Spinner
        as="output"
        animation="border"
        size="sm"
        aria-hidden="true"
        />
);

export const readUploadedFile = async (file, asJson=false) => { // reads file from file input as text or json
    return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
            const text = reader.result?.replaceAll(/\r\n?/g, '\n'); // sanitize CRLF sequences
            if(!asJson) {
                resolve(text);
                return;
            }

            try {
                const content = JSON.parse(text);
                resolve(content);
            } catch (error) {
                console.debug('Failed to parse JSON from file:', error);
                resolve(null);
            }
        };

        reader.onerror = () => {
            resolve(null);
        };

        reader.readAsText(file, "UTF-8");
    });
}

export const getEpochFromTle = (tle) => {
    let line1 = tle?.line1;
    if(!line1?.length || line1.startsWith("$")) return null;
    try {
        if (line1.startsWith("'") || line1.startsWith("\"")) {
            line1 = line1.slice(1); // if it comes with quotes
        }
        const year = Number.parseInt("20" + line1.slice(18, 20));
        const decimalDays = Number.parseFloat(line1.slice(20, 32));
        const millis = (decimalDays-1) * 24 * 60 * 60 * 1000;

        const yearDate = Date.UTC(year, 0, 1);
        const date = new Date(yearDate + millis);

        return date;
    } catch {
        return null;
    }
};

export const setGuestCookie = () => {
  const cookieDomain = globalThis.location.hostname;
  Cookies.set('SBMS_GUEST', true, { secure: true, sameSite:'Strict', domain: cookieDomain});
};

export const hasGuestCookie = () => {
  return Cookies.get('SBMS_GUEST');
};

export const clearGuestCookie = () => {
  const cookieDomain = globalThis.location.hostname;
  Cookies.remove('SBMS_GUEST', { domain: cookieDomain });
};

export const logoutUser = async (user, getBaseUrl, navigate, setUser) => {
    if(user.isGuest) {
        clearGuestCookie();
    }
    else {
        const baseUrl = getBaseUrl(false);
        await fetch(`${baseUrl}/logout`, {
            method: 'POST',
            credentials: 'include',
        });
    }

    setUser({
        loggedIn: false,
        uuid: null,
        access_token: null,
        isGuest: false,
        isSuperuser: false,
        lastLoggedIn: -1,
    });
    
    navigate("/");
};

export const getSpaceCockpitUrl = (accessToken, scQueryParams=null, includeExerciseData=false) => {
    let url = `${getDomainUrl('spacecockpit')}/?datamode=${includeExerciseData ? 'REAL,EXERCISE,SIMULATED,TEST' : 'REAL'}`;
    if (accessToken) {
        url += `&token=${accessToken}`;
    }
    if (scQueryParams) {
        url += (accessToken && scQueryParams.startsWith("?")) ? ("&" + scQueryParams.slice(1)) : scQueryParams;
    }
    
    url += "&extAssetPicker=true"; // SC will prioritize SBMS catalog picker
    
    return url;
}

export const accordionBodyClass = "p-0 mt-3 ps-4 pe-4 text-white customscrollbar";

export const isValidPwd = (s) => {
    if (!s || s.length < 8) return false; // min length
    const reqs = [/\d/, /[a-z]/, /[A-Z]/, /[^\da-zA-Z]/]; // one each of number, lowercase, uppercase, special character
    return reqs.every((item) => item.test(s));
};

// Determine whether background color is darker or lighter
// https://stackoverflow.com/questions/3942878/how-to-decide-font-color-in-white-or-black-depending-on-background-color
export const colorIsDark = (bgColor) => { 
    let r, g, b;

    if(bgColor.charAt(0) === '#') { // #FFFF00
        const color = bgColor.substring(1, 7);
        r = Number.parseInt(color.substring(0, 2), 16); // hexToR
        g = Number.parseInt(color.substring(2, 4), 16); // hexToG
        b = Number.parseInt(color.substring(4, 6), 16); // hexToB
    }
    else { // rgb(255, 255, 0)
        const rgb = bgColor.replaceAll("rgb(", "").replaceAll(")", "").split(",");
        r = Number.parseInt(rgb[0].trim());
        g = Number.parseInt(rgb[1].trim());
        b = Number.parseInt(rgb[2].trim());
    }

    const uicolors = [r / 255, g / 255, b / 255];
    const c = uicolors.map((col) => {
    if (col <= 0.03928) {
        return col / 12.92;
    }
    return Math.pow((col + 0.055) / 1.055, 2.4);
    });

    const L = (0.2126 * c[0]) + (0.7152 * c[1]) + (0.0722 * c[2]); // luminance
    return L <= 0.179; // W3C recommended threshold
};

export const convertMarkdownLineBreaks = (str) => str.replaceAll(/\n/gi, "  \n&nbsp;");

export const getMotdTooltipText = (motd) => {
    let tooltip = "";
    if(motd.Description) tooltip += `${motd.Description}\n\n`;
    if(motd.EndTime) {
        tooltip += `*Effective until ${formatDate(motd.EndTime)} (${getRelativeTime(motd.EndTime)})*`;
    }
    return tooltip;
};

export const isDefined = (val) => val || val === 0;

export const REGIMES = {
    "LEO": 2,
    "MEO": 16,
    "HEO": 4,
    "GEO Inclined": 8,
    "Polar": 128,
    "SSO": 64,
    "GEO Stationary": 256,
    "GEO Drifter": 512,
    "Molniya": 32,
    "Undetermined": 1,
};

export const getRegime = (num) => {
    return Object.keys(REGIMES).find((regime) => REGIMES[regime] === num);
};

export const setStateWrapper = (valueOrFunc, setState, callback, transformation=null) => {
    if(typeof(valueOrFunc) === "function") { // function
        setState((current) => {
            let newValue = valueOrFunc(current);
            if(transformation) newValue = transformation(newValue);

            callback(newValue);
            return newValue;
        });
    }
    else { // value
        let newValue = valueOrFunc;
        if(transformation) newValue = transformation(newValue);

        setState(newValue);
        callback(newValue);
    }
}

export const nestedTernary = (condition1, condition2, return1, return2, return3) => {
    // for replacing a nested ternary like (c1 ? (c2 ? r1 : r2) : r3) with something Sonarqube doesn't complain about
    if(condition1) {
        return condition2 ? return1 : return2;
    }
    return return3;
};

export const safeParseFloat = (str) => {
    try {
        if(!str?.length) return null;
        const num = Number.parseFloat(str);
        return Number.isNaN(num) ? null : num;
    } catch {
        return null;
    }
};

export const validateTle = async (tle, setValidation) => {
    try {
        const response = await fetch(`${getBaseUrl()}/validate-tle`, {
            method: "POST",
            credentials: 'include',
            headers: {
                "Content-Type": "text/plain",
            },
            body: tle.line1 + "\n" + tle.line2,
        });

        if(!response.headers.get("content-type")?.includes("application/json")) return;
        const body = await response.json();

        if (!response.ok && response.status !== 200) {
            if(body?.pairs?.[0] && !body.ok) {
                setValidation({
                    success: false,
                    text: body.pairs[0].errors.join(", ")
                });
            }
        }
        else if(body?.ok) {
            setValidation({
                success: true,
                text: "TLE validated"
            });
        }
        
    } catch (error) {
        console.error('API Error:', error);
    }
};

export const compareValues = (a, b, ascending) => {
    const dir = ascending ? 1 : -1;

    if (a == null && b == null) return 0;
    if (a == null) return dir;
    if (b == null) return -dir;

    if (typeof a === 'number' && typeof b === 'number') {
        return dir * (a - b);
    }
    if (a instanceof Date && b instanceof Date) {
        return dir * (a - b);
    }
    return dir * String(a).localeCompare(String(b));
};

export const setLoggedInUser = (data, setUser) => {
    setUser({
        loggedIn: true,
        uuid: data.uuid,
        access_token: data.jwt,
        CacId: data.cacId,
        isGuest: false,
        username: data.username,
        lastLoggedIn: Date.now(),
        email: data.email,
        isSuperuser: data.superuser,
    });
};